import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Copy, Crop, Instagram, Maximize, Megaphone, Pencil, Play } from 'lucide-react';
import { useSidebarColapsable } from './useSidebarColapsable';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { SidebarContext, useSidebar } from '../../context/SidebarContext';
import { CreditsTopBar } from '../../components/CreditsTopBar';
import { CreditsPricingSheet } from '../../components/pricing/CreditsPricingSheet';
import {
  copyPricingPorMotivo,
  type MotivoPricing,
} from '../../components/pricing/pricingContexts';
import { CAPACIDAD_POR_PLAN, estadoDeCreditos } from '../../lib/creditsStatus';
import {
  CARRUSEL_MODOS,
  CREACIONES_RECIENTES,
  elegirFilasPlantillas,
  TODAS_LAS_HERRAMIENTAS,
  saludo,
  type HerramientaAtlas,
  type ItemGaleria,
} from './contenidoAtlas';

/**
 * Estilo 3 — **Minimal** (referencia: Vidnoz). ADR-2026-07-30-tres-estilos-interfaz-shells-propios.
 *
 * Shell claro y vacío: sidebar flotante con lo mínimo, saludo de bienvenida, carrusel
 * de modos y el resto del espacio libre.
 *
 * A diferencia de la captura de referencia —que muestra "Recent videos" vacío— acá el
 * bloque de creaciones recientes va CON contenido real (ADR-2026-07-30-contenido-previews-sale-catalogos-existen): con el bloque vacío,
 * este estilo se compara contra los otros dos con media pantalla en blanco y la
 * comparación deja de ser justa. Sólo dev.
 *
 * Viene en dos variantes de superficie (ADR-2026-07-30-minimal-gris-variante-superficie-prop), que es lo ÚNICO que cambia entre
 * ellas: mismo layout, mismo contenido, mismo copy. Se resuelve por prop y no
 * duplicando la pantalla porque este estilo se sigue iterando: un clon habría quedado
 * desfasado al primer cambio.
 */

/** Qué superficie es fondo y cuál es figura. Lo único que separa a las dos variantes. */
export type VarianteMinimal = 'claro' | 'gris';

interface TemaMinimal {
  /** Fondo de la pantalla. */
  fondo: string;
  /** Sidebar. */
  panel: string;
  /** Bloques de contenido: tarjetas de sección y celdas de imagen. */
  tarjeta: string;
  /** Un escalón por encima de `tarjeta`: chips de ícono y la tarjeta de cuenta. */
  hueco: string;
  /** Celda "Ver más": vacía y punteada, va un tono por debajo del resto. */
  vacio: string;
  /** Fondo del ítem de sidebar seleccionado. Sigue la misma inversión que el panel. */
  seleccion: string;
}

const TEMAS: Record<VarianteMinimal, TemaMinimal> = {
  // Original: lienzo gris, contenido en blanco.
  claro: {
    fondo: 'bg-[#F6F6F7]',
    panel: 'bg-white',
    tarjeta: 'bg-white',
    hueco: 'bg-[#F2F3F5]',
    vacio: 'bg-[#FAFAFB]',
    // Sobre panel blanco el seleccionado necesita color propio: el tinte de marca.
    seleccion: 'bg-[#EBEFFE]',
  },
  // Invertida: lienzo blanco, contenido en gris. Los grises son los mismos de la
  // variante clara, cambiados de rol — no una paleta nueva.
  gris: {
    fondo: 'bg-white',
    panel: 'bg-[#F1F2F4]',
    tarjeta: 'bg-[#F4F5F7]',
    hueco: 'bg-white',
    vacio: 'bg-white',
    // Sobre panel gris alcanza con el blanco: la pastilla resalta por contraste de
    // superficie, sin sumar un color más. El ícono y el texto siguen en azul de marca.
    seleccion: 'bg-white',
  },
};

/**
 * Identidad del usuario — **una sola fuente** (ADR-2026-07-31-avatar-pieza-sola-identidad-sola). El nombre completo manda: de
 * él salen el nombre del saludo (el primero) y la inicial del avatar, así cambiar el
 * nombre en un solo lugar actualiza los tres. Cuando esto lea sesión, `NOMBRE_COMPLETO`
 * pasa a venir de `auth.user()` y lo demás sigue derivándose igual.
 *
 * Fijos por ahora por lo mismo que el plan y los créditos: el preview no lee sesión
 * (ADR-2026-07-30-contenido-previews-sale-catalogos-existen).
 */
const NOMBRE_COMPLETO = 'Maxi Cris';
const EMAIL_USUARIO = 'maxi@estudio-atlas.com';

/** Nombre de pila para el saludo. */
const NOMBRE_USUARIO = NOMBRE_COMPLETO.trim().split(/\s+/)[0];

/**
 * Inicial del avatar. Del nombre completo y en mayúscula, sin asumir que ya lo esté:
 * si mañana el nombre viene de un formulario, "maxi" tiene que seguir dando "M".
 */
const INICIAL_USUARIO = (NOMBRE_COMPLETO.trim()[0] ?? '?').toUpperCase();

/**
 * Avatar del usuario — ADR-2026-07-31-avatar-pieza-sola-identidad-sola. Tinte de marca de fondo y la inicial en el azul 500,
 * el mismo par que usan el ítem de sidebar seleccionado y el chip de marca.
 *
 * Es un componente y no una clase suelta porque aparece en cuatro lugares (saludo,
 * tarjeta de cuenta, sidebar colapsada y cabecera del menú) y tienen que cambiar
 * juntos: antes el del saludo era un degradé y los otros tres un círculo negro.
 */
function AvatarUsuario({
  px = 36,
  cuerpo = 13,
  className = '',
}: {
  px?: number;
  cuerpo?: number;
  className?: string;
}) {
  return (
    <span
      style={{ width: px, height: px, fontSize: cuerpo }}
      className={`grid shrink-0 place-items-center rounded-full bg-[#EBEFFE] font-bold text-brand-primary ${className}`}
    >
      {INICIAL_USUARIO}
    </span>
  );
}

/**
 * Fallback del selector de marca: **sólo** se ve si no hay sesión o la cuenta no
 * tiene marca ni empresa cargada. Con sesión, el selector muestra la marca activa
 * real y su rubro (ADR-2026-07-31-rubro-selector-sale-marca-real).
 *
 * El rubro es una de las categorías reales del alta y no un string inventado, así
 * el fallback tiene el largo que va a tener el dato de verdad.
 */
const MARCA_EJEMPLO = { nombre: 'Casa Aurora', rubro: 'Hogar y decoración' };

/**
 * Plan, créditos y descuento. Fijos: esta pantalla es una maqueta de diseño y sin
 * sesión no hay `company_profiles` ni cupón que leer. Para ver otro caso, cambiar
 * estos valores acá (antes se simulaban desde el panel CONTEXTO DEV, eliminado en
 * ADR-2026-08-03-baja-panel-contexto-dev).
 */
const PLAN_DEFAULT = 'gratis';
const CREDITOS_DEFAULT = 0;
const DESCUENTO_DEFAULT = 50;

/** `company_profiles.plan` → etiqueta, igual que el Sidebar de la app real. */
const ETIQUETA_PLAN: Record<string, string> = {
  gratis: 'Plan Gratis',
  emprendimiento: 'Plan Básico',
  negocio: 'Plan Pro',
  enterprise1: 'Plan Ultra',
  enterprise2: 'Plan Ultra',
  enterprise3: 'Plan Ultra',
};

/* La capacidad por plan —el denominador del umbral del 5% (ADR-2026-07-30-solo-criterio-saldo-toda-app)— vive en
   `lib/creditsStatus` desde ADR-2026-07-31-contador-sidebar-habla-creditos-sale: estaba copiada acá y en `useHomeBanners`. */

/**
 * Nav de la sidebar. Apunta a las rutas NUEVAS (ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo): las tres montan la
 * pantalla real dentro de este mismo shell, en vez de saltar al shell viejo.
 */
const NAV_LATERAL = [
  { label: 'Inicio', icono: 'home', ruta: '/inicio' },
  { label: 'Creaciones', icono: 'photo_library', ruta: '/creaciones' },
  { label: 'Editar marca', icono: 'shopping_bag', ruta: '/editar-marca' },
];

/**
 * Salida del fade entre secciones (ADR-2026-07-31-ruta-layout-shell-sidebar-deja, afinado en ADR-2026-07-31-ajustes-shell-padding-secciones-hospedadas). El fade es
 * **asimétrico** a propósito: la salida es corta —lo que ya no sirve tiene que
 * irse rápido, si no el click se siente trabado— y la entrada larga (680ms, en la
 * clase del wrapper) para que la sección nueva aparezca de a poco.
 *
 * Duplicado respecto de los valores originales (120/340) a pedido: el fade tenía
 * que sentirse más sutil todavía.
 *
 * Sólo la salida es una constante: la entrada la aplica Tailwind y sus clases
 * tienen que ser literales para que el JIT las genere, así que interpolarla acá
 * no serviría.
 */
const FADE_SALIDA_MS = 240;

/**
 * Estado seleccionado de un ítem de sidebar: ícono y texto en el azul 500 de la marca
 * (`brand.500` / `--brand-primary`, #1644EB). El fondo lo pone el tema (`seleccion`),
 * que cambia con la variante.
 */
const ITEM_ACTIVO = 'text-brand-primary';

/**
 * Herramientas de la sidebar con **Plantillas al final**: no es un modo de
 * generación como los demás, es un atajo al catálogo (que además tiene su propia
 * sección en la home). Se reordena acá y no en `TODAS_LAS_HERRAMIENTAS` para no
 * mover la fila de íconos del estilo Workspace, que la consume por índice.
 */
const HERRAMIENTAS_SIDEBAR = [
  ...TODAS_LAS_HERRAMIENTAS.filter((h) => h.id !== 'plantillas'),
  ...TODAS_LAS_HERRAMIENTAS.filter((h) => h.id === 'plantillas'),
];

/**
 * Ruta de cada herramienta DENTRO de este shell — ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell.
 *
 * El `ruta` que trae `contenidoAtlas` apunta al shell viejo (`/interfaz-v2/...`,
 * `/editor-video`), que monta `DashboardLayout` con su propia sidebar. Acá se
 * traduce al equivalente hospedado, que renderiza la MISMA pantalla pero como hija
 * de este layout — igual que `/creaciones` y `/editar-marca` (ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo).
 *
 * Se mapea por `id` y no se toca `contenidoAtlas` por el mismo motivo que las
 * etiquetas (ADR-2026-07-31-solo-nombre-modo-sidebar-toma): esas rutas son las correctas para `EstiloWorkspace`, que no
 * vive en este shell.
 */
const RUTA_EN_SHELL: Record<string, string> = {
  plantillas: '/plantillas',
  'editor-video': '/estudio-video',
};

/**
 * Los MODOS no se listan uno por uno a propósito: `/modo/:mode` es una ruta con
 * parámetro, así que la traducción es puramente mecánica. Enumerarlos hacía que un
 * modo nuevo —`catalogo-muebles` e `infografia` llegaron así— cayera al `??` y se
 * fuera al shell viejo sin que nadie lo notara.
 */
const RUTA_MODO_VIEJA = /^\/interfaz-v2\/modo\/([^/]+)$/;

/** Destino de un ítem de herramientas: el hospedado si existe, si no el original. */
function rutaSidebar(h: { id: string; ruta: string }): string {
  const fijo = RUTA_EN_SHELL[h.id];
  if (fijo) return fijo;
  const modo = h.ruta.match(RUTA_MODO_VIEJA);
  return modo ? `/modo/${modo[1]}` : h.ruta;
}

/**
 * El historial, en su ruta hospedada — ADR-2026-07-31-fail-closed-real-catalogo-enlaces.
 *
 * `/creaciones` y `/redes-sociales/imagenes` montan el MISMO
 * `GeneratedImagesHistory`; la diferencia es el shell. Los enlaces de esta pantalla
 * apuntaban al segundo, así que "Ver todo" y las ediciones rápidas sacaban al
 * usuario a `DashboardLayout` —otra sidebar, sin el fade— desde adentro de `/inicio`.
 */
const RUTA_HISTORIAL = '/creaciones';

/**
 * Ancho de la sidebar flotante, y el margen que la separa del borde y del contenido
 * (`m-2` = 8px). Se nombran porque además del `<aside>` los consume el override de
 * `SidebarContext` (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell): las pantallas hospedadas anclan paneles `fixed` a
 * `sidebarWidth`, y ese número tiene que ser el borde DERECHO de esta sidebar
 * —margen + ancho + margen— y no el ancho a secas.
 */
const ANCHO_SIDEBAR = 244;
const ANCHO_SIDEBAR_COLAPSADA = 66;
const MARGEN_SIDEBAR = 8;

/* El motivo por el que se abre el catálogo (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo) y su copy viven en
   `pricingContexts` desde ADR-2026-07-31-copy-catalogo-sigue-saldo-real: la tabla estaba duplicada acá y en
   `SubscriptionLimitModal`, y agregar un motivo arreglaba uno solo. */

/**
 * Abre el catálogo como overlay. Lo provee el shell para que lo consuma cualquier
 * descendiente, incluidas las secciones montadas en el `<Outlet />`: el estado del
 * overlay tiene que ser del shell, porque si viviera en la sección cambiar de
 * pantalla lo cerraría.
 *
 * Lleva además el **estado de cuenta** (plan y créditos) porque lo consume la misma
 * gente que la acción: el encabezado de la home muestra los créditos al lado del CTA
 * de mejora (ADR-2026-07-31-creditos-mejora-plan-encabezado-home), y esos números viven
 * en el shell. Pasarlos por props obligaría a que cada sección hospedada los reciba y
 * los reenvíe.
 *
 * Los defaults son inertes para que un consumidor montado fuera del shell no explote.
 */
interface CuentaCtx {
  abrirPricing: (motivo?: MotivoPricing) => void;
  creditos: number;
  planLabel: string;
  /**
   * `true` cuando no hay nada que resolver: plan pago **y** saldo por encima del
   * umbral. Es el criterio que decide si el CTA de mejora va sólido o en tinte
   * (ADR-2026-07-31-columna-contenido-90-mejorar-plan); se calcula una vez en el shell para que los dos botones —el de la
   * sidebar y el del encabezado— no puedan discrepar.
   */
  planAlDia: boolean;
}

const CuentaContext = createContext<CuentaCtx>({
  abrirPricing: () => {},
  creditos: 0,
  planLabel: '',
  planAlDia: false,
});

/**
 * Clases del CTA "Mejorar plan" según ese criterio. Sólido cuando hay algo que
 * resolver; en tinte azul con texto de marca cuando no —a un cliente pago con
 * créditos de sobra el botón no le está pidiendo nada—.
 */
function clasesMejorarPlan(planAlDia: boolean): string {
  return planAlDia
    ? 'bg-[#EBEFFE] text-brand-primary hover:bg-[#DFE6FD]'
    : 'bg-brand-primary text-white hover:bg-brand-primary-hover';
}

/** Soporte de Atlas — el mismo número que usa el resto de la app. */
const WHATSAPP_URL = 'https://wa.me/5491122635392';

/**
 * "Ediciones rápidas": las acciones que hoy aparecen al abrir una imagen recién
 * generada (la barra de `GeneratedImagesInline`). Mismos íconos —los lucide que usa
 * esa barra— y mismos títulos; la descripción se agrega acá, que en la barra no entra.
 *
 * Las rutas apuntan a donde vive de verdad cada acción: las que necesitan una imagen
 * abierta llevan al historial, que es donde se abre.
 */
const EDICIONES_RAPIDAS = [
  {
    label: 'Editar',
    desc: 'Cambiá lo que quieras con un prompt',
    Icono: Pencil,
    // Al historial como sus hermanas, y no a `/redes-sociales/editar-imagen`:
    // esa pantalla necesita una imagen abierta (`imageIdParam`) que desde acá no
    // existe, y además vive sólo bajo `DashboardLayout`, así que sacaba al usuario
    // del shell. El historial es donde se abre una imagen para editarla.
    ruta: RUTA_HISTORIAL,
  },
  { label: 'Carrusel', desc: 'Más tomas del mismo producto', Icono: Copy, ruta: RUTA_HISTORIAL },
  { label: 'Animar', desc: 'Convertí la foto en video', Icono: Play, ruta: RUTA_HISTORIAL },
  {
    label: 'Anuncio',
    desc: 'Sumale texto y formato de aviso',
    Icono: Megaphone,
    ruta: '/modo/anuncio',
  },
  {
    label: 'Descripción',
    desc: 'El texto listo para el post',
    Icono: Instagram,
    ruta: RUTA_HISTORIAL,
  },
  { label: 'Recortar', desc: 'Ajustá el encuadre', Icono: Crop, ruta: RUTA_HISTORIAL },
  {
    label: 'Mejorar',
    desc: 'Subí la resolución con IA',
    Icono: Maximize,
    ruta: RUTA_HISTORIAL,
  },
];

/**
 * Cada cuánto pasa solo de un modo al siguiente. Es también el techo de reproducción
 * de las carátulas en video (ver `CardModo`): duran ~6 s y se cortan acá, así ninguna
 * sigue corriendo después de que la tira ya avanzó.
 */
const MS_POR_MODO = 5000;

/**
 * Apertura del hueco de la bajada en las cards de modo (ADR-2026-07-31-titulo-cards-modo-apoya-abajo): misma curva y
 * duración que la expansión de la card, para que se lea como parte de la card
 * creciendo y no como una animación de texto suelta.
 *
 * La OPACIDAD no va acá porque es asimétrica (ADR-2026-07-31-bajada-cards-modo-aparece-hueco): `truncate` le pone
 * `overflow: hidden` a la bajada, así que mientras el hueco se abre el texto se ve
 * recortado por arriba. Entrando, la opacidad espera a que el hueco dé la altura de
 * una línea (17,2px de los 20px finales, que con esta curva llega al ~58% de los
 * 700ms) y recién ahí aparece. Saliendo hace lo contrario: se apaga rápido, antes de
 * que el hueco se cierre y la recorte.
 */
const TRANSICION_ALTO_BAJADA =
  'max-height 700ms cubic-bezier(0.4, 0, 0.2, 1), margin-top 700ms cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * Copy de la tira, al mínimo: sobre la imagen no hay lugar para el título largo ni
 * para la descripción completa del catálogo, y compitiendo con la foto se leen peor.
 * Nombre corto + una línea de qué hace. El texto largo sigue vivo en `MODES`, que es
 * lo que muestran `/modos` y la home real.
 */
const COPY_TIRA: Record<string, { titulo: string; bajada: string }> = {
  // "Automática" y "Modelo" en vez de "Imagen automática" / "Imagen con modelo": en la
  // card angosta quedan 82 px de texto y esos títulos miden 130 y 134 px, así que
  // envolvían en dos líneas y su bloque arrancaba 17 px más arriba que el de los
  // cortos — desalineados contra las otras cuatro. Con todos en una línea, la
  // alineación sale sola. Estos títulos son ahora el nombre del modo también en la
  // sidebar (ADR-2026-07-31-solo-nombre-modo-sidebar-toma); el largo del catálogo ya no se muestra en esta pantalla
  // —estaba en el `title` de la card y se sacó en ADR-2026-07-31-tira-modos-muestra-tooltip-nativo—.
  auto: { titulo: 'Automática', bajada: 'La IA arma la escena' },
  modelo: { titulo: 'Modelo', bajada: 'Sobre un modelo IA' },
  catalogo: { titulo: 'Catálogo', bajada: 'Varias vistas en una tanda' },
  'video-influencer': { titulo: 'Video', bajada: 'Un influencer IA lo presenta' },
  asistente: { titulo: 'Asistente', bajada: 'Pedile contenido por chat' },
  anuncio: { titulo: 'Anuncio', bajada: 'Listo para publicar' },
};

/**
 * Etiqueta de un modo en la sidebar — ADR-2026-07-31-solo-nombre-modo-sidebar-toma.
 *
 * Sale de `COPY_TIRA`, la MISMA fuente que los títulos del carrusel, para que un
 * modo se llame igual en las dos partes de la pantalla. Antes la sidebar usaba
 * `etiquetaMenu` del catálogo ("Generar imágenes", "Imagen con modelo") mientras
 * la tira decía "Automática" y "Modelo": dos nombres para el mismo destino, a la
 * vista al mismo tiempo.
 *
 * Se deriva acá y no se toca `contenidoAtlas`: `etiquetaMenu` lo consume también
 * `EstiloWorkspace`, y esos títulos largos siguen siendo los correctos para un
 * menú que no tiene un carrusel al lado con el nombre corto.
 *
 * El fallback a `nombre` cubre a Plantillas y Estudio de Video, que no están en el
 * carrusel (ADR-2026-07-30-minimal-plantillas-categoria-creaciones-fila) y por lo tanto no tienen entrada en `COPY_TIRA`.
 */
function etiquetaSidebar(h: { id: string; nombre: string }): string {
  return COPY_TIRA[h.id]?.titulo ?? h.nombre;
}

/**
 * Ítems del menú de cuenta — ADR-2026-07-31-menu-cuenta-avatar-uno-solo. "Cerrar sesión" NO está acá: va aparte, detrás
 * del divisor, porque es la única acción destructiva del menú.
 *
 * "Centro de ayuda" repite el destino del ítem homónimo de la sidebar (WhatsApp de
 * soporte) en vez de ir a `/ayuda`: dentro de esta pantalla el centro de ayuda es
 * ese, y dos etiquetas iguales con destinos distintos serían un problema peor.
 */
const ITEMS_MENU_CUENTA: { label: string; icono: string; ruta?: string; url?: string }[] = [
  { label: 'Perfil', icono: 'person', ruta: '/perfil' },
  { label: 'Suscripción', icono: 'credit_card', ruta: '/subscription' },
  { label: 'Centro de ayuda', icono: 'support_agent', url: WHATSAPP_URL },
];

/**
 * Menú de cuenta que se abre desde el avatar — ADR-2026-07-31-menu-cuenta-avatar-uno-solo.
 *
 * Un solo componente para los dos avatares (el del saludo, arriba a la derecha, y el
 * de la tarjeta de cuenta, abajo en la sidebar): son el mismo menú, y duplicarlo
 * garantizaba que se desincronizaran.
 *
 * Se replica la ESTRUCTURA de la referencia —cabecera con nombre y mail, ítems con
 * ícono, divisor y cerrar sesión al final— no su paleta: la referencia es oscura
 * porque esa app lo es, y acá el shell es claro.
 *
 * `variante` decide el anclaje, que es lo único que cambia entre los dos:
 * - `home`: cuelga del avatar, alineado a la derecha. `absolute`, así acompaña al
 *   contenido si la página scrollea.
 * - `sidebar`: sale hacia la derecha del avatar y crece hacia arriba. Va `fixed` y
 *   con coordenadas medidas al abrir, porque el `<aside>` tiene `overflow-hidden`
 *   y un hijo absoluto quedaría cortado por el borde del panel.
 */
function MenuCuenta({
  variante,
  children,
  etiqueta = 'Abrir menú de cuenta',
}: {
  variante: 'home' | 'sidebar';
  children: ReactNode;
  etiqueta?: string;
}) {
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState<{ left: number; bottom: number } | null>(null);
  const contenedor = useRef<HTMLDivElement | null>(null);
  const disparador = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!abierto) return;
    const afuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto]);

  const alternar = () => {
    if (!abierto && variante === 'sidebar' && disparador.current) {
      const r = disparador.current.getBoundingClientRect();
      // Se ancla al borde derecho del PANEL, no al del avatar: colgado del avatar,
      // con la sidebar expandida el menú caía encima de la lista de herramientas.
      const panel = disparador.current.closest('aside')?.getBoundingClientRect();
      // Crece hacia ARRIBA desde el pie del avatar: en la sidebar el disparador está
      // abajo de todo y un menú que baje se saldría de la ventana.
      setCoords({ left: (panel?.right ?? r.right) + 10, bottom: window.innerHeight - r.bottom });
    }
    setAbierto((v) => !v);
  };

  const cerrarYNavegar = (ruta: string) => {
    setAbierto(false);
    navigate(ruta);
  };

  const claseItem =
    'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-[13.5px] font-[500] text-black/75 transition-colors hover:bg-black/[0.04] hover:text-black';

  return (
    <div ref={contenedor} className="relative">
      <button
        ref={disparador}
        type="button"
        onClick={alternar}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={etiqueta}
        className="block rounded-full"
      >
        {children}
      </button>

      {abierto && (
        <div
          role="menu"
          style={
            variante === 'sidebar' && coords
              ? { position: 'fixed', left: coords.left, bottom: coords.bottom }
              : undefined
          }
          className={`animate-fade-in z-50 w-[268px] rounded-2xl border border-black/[0.08] bg-white p-1.5 shadow-[0_16px_44px_rgba(16,24,45,0.16)] ${
            variante === 'home' ? 'absolute right-0 top-full mt-2' : ''
          }`}
        >
          {/* Cabecera: quién sos. Sin acción propia —en la referencia tampoco la
              tiene—; "Perfil" es el ítem que lleva a editarlo. */}
          <div className="flex items-center gap-2.5 px-2.5 pb-2 pt-2.5">
            <AvatarUsuario px={40} cuerpo={14} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-semibold leading-tight">{NOMBRE_COMPLETO}</p>
              <p className="mt-0.5 truncate text-[12.5px] leading-tight text-black/45">
                {EMAIL_USUARIO}
              </p>
            </div>
          </div>

          <div className="mt-1 flex flex-col gap-0.5">
            {ITEMS_MENU_CUENTA.map((item) =>
              item.url ? (
                <a
                  key={item.label}
                  role="menuitem"
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setAbierto(false)}
                  className={claseItem}
                >
                  <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
                    {item.icono}
                  </span>
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.label}
                  role="menuitem"
                  type="button"
                  onClick={() => cerrarYNavegar(item.ruta!)}
                  className={claseItem}
                >
                  <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
                    {item.icono}
                  </span>
                  {item.label}
                </button>
              ),
            )}
          </div>

          <div className="my-1.5 h-px bg-black/[0.07]" />

          {/* Cierre de sesión REAL, el mismo que el Sidebar de producción
              (`supabase.auth.signOut()` + `/auth`): es la única parte del menú que no
              puede ser decorativa sin volverse mentirosa. */}
          <button
            role="menuitem"
            type="button"
            onClick={async () => {
              setAbierto(false);
              await supabase.auth.signOut();
              navigate('/auth');
            }}
            className={claseItem}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
              logout
            </span>
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Una card de la tira. Es un componente aparte para poder manejar el `<video>` por
 * ref: la reproducción no se puede dejar en props (`autoPlay` sólo cuenta al montar)
 * si además hay que **frenar al terminar**.
 *
 * El video se reproduce UNA vez al volverse activo y se queda en el último cuadro.
 * No hay `loop`: aunque el mouse siga encima, no vuelve a arrancar; sólo se rebobina
 * cuando la card deja de estar activa.
 */
function CardModo({
  modo,
  esActivo,
  onActivar,
  onIr,
  tema,
}: {
  modo: HerramientaAtlas;
  esActivo: boolean;
  onActivar: () => void;
  onIr: () => void;
  tema: TemaMinimal;
}) {
  const video = useRef<HTMLVideoElement | null>(null);
  const copy = COPY_TIRA[modo.id] ?? { titulo: modo.nombre, bajada: '' };

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    if (esActivo) {
      el.currentTime = 0;
      el.play().catch(() => {
        /* autoplay bloqueado: queda el primer cuadro */
      });
    } else {
      el.pause();
      el.currentTime = 0;
    }
  }, [esActivo]);

  return (
    <button
      onClick={onIr}
      onMouseEnter={onActivar}
      onFocus={onActivar}
      aria-current={esActivo}
      // Sin `title`: el atributo nativo dispara el globito gris del navegador al
      // dejar el mouse quieto sobre la card, tapando la imagen justo cuando se la
      // está mirando (ADR-2026-07-31-tira-modos-muestra-tooltip-nativo). El nombre del modo ya está impreso sobre la card,
      // así que el botón conserva su nombre accesible por contenido.
      // El reparto y su animación van INLINE, no en clases: `index.css` tiene una
      // regla global para `button:not(:disabled)` que fija `transition-property`
      // (una lista sin `flex-grow`) y 150ms de duración. Es un selector de elemento,
      // así que le gana a las utilidades de Tailwind y la transición no corría.
      style={{
        flexGrow: esActivo ? 2.8 : 0.62,
        flexShrink: 1,
        flexBasis: 0,
        transition: 'flex-grow 700ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      className={`group relative overflow-hidden rounded-2xl border border-black/[0.06] text-left ${tema.hueco}`}
    >
      {modo.video ? (
        <video
          ref={video}
          src={modo.video}
          // Sin `poster` (Anuncio) el navegador dibuja el primer cuadro del propio
          // video, que es justo la portada que se quiere.
          poster={modo.poster || undefined}
          muted
          playsInline
          preload={modo.poster ? 'none' : 'metadata'}
          // Techo de reproducción: aunque el archivo dure más, se frena al llegar al
          // tiempo que la tira le da a cada modo. Sin esto, con el mouse encima (que
          // pausa la rotación) el video seguiría hasta el final.
          onTimeUpdate={(e) => {
            const v = e.currentTarget;
            if (v.currentTime >= MS_POR_MODO / 1000) v.pause();
          }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <img
          src={modo.poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}

      {/* Velo NEGRO sobre las que no están en foco: oscurece de verdad. Bajarle la
          opacidad a la card entera, sobre este fondo claro, las aclara en vez de
          oscurecerlas. Va encima de la imagen y debajo del texto, así el label sigue
          leyéndose. */}
      <span
        aria-hidden="true"
        className={`absolute inset-0 bg-black transition-opacity duration-500 ${
          esActivo ? 'opacity-0' : 'opacity-60'
        }`}
      />

      {/* Degradé sólo para que el texto se lea; se refuerza en el activo. El padding
          es el MISMO en los dos estados: con `px-3` colapsado y `px-4` activo, el
          texto se corría 4 px al expandirse y las cards quedaban desalineadas entre
          sí. Lo que cambia con el estado es sólo la fuerza del degradé. */}
      <span
        className={`absolute inset-x-0 bottom-0 flex items-end gap-3 bg-gradient-to-t to-transparent px-4 pb-3.5 pt-12 ${
          esActivo ? 'from-black/75 via-black/30' : 'from-black/60 via-black/15'
        }`}
      >
        <span className="min-w-0 flex-1">
          {/* Cuerpo FIJO, sin transición: el título es el mismo texto en los dos
              estados, así que cualquier cambio de tamaño se ve como movimiento y no
              como aparición.

              `truncate` es acá una GARANTÍA de alineación, no un recorte esperado: los
              seis títulos entran en una línea a este ancho (ver `COPY_TIRA`), y esto
              impide que una pantalla más angosta devuelva el wrap a dos líneas —que es
              lo que descolocaba unas cards contra otras—. Si alguna vez recorta, es
              señal de que el título es largo de más para la tira. */}
          <span className="block truncate text-[14px] font-semibold leading-tight text-white">
            {copy.titulo}
          </span>
          {/* La bajada NO reserva su línea cuando la card está colapsada: si la
              reservara, el título quedaría 21 px por encima del borde en las seis
              cards angostas en vez de apoyado abajo.

              El costo es que el título sube al activarse la card, para hacerle lugar.
              Se le da la MISMA curva y duración que a la expansión de la card
              (700 ms) para que se lea como parte de la card creciendo y no como una
              animación de texto aparte; la opacidad va más corta, así el texto
              termina de aparecer cuando el hueco ya está abierto. */}
          {copy.bajada && (
            <span
              style={{
                transition: esActivo
                  ? `${TRANSICION_ALTO_BAJADA}, opacity 280ms ease-out 420ms`
                  : `${TRANSICION_ALTO_BAJADA}, opacity 160ms ease-in`,
              }}
              className={`block truncate text-[12.5px] leading-snug text-white/75 ${
                esActivo ? 'mt-1 max-h-5 opacity-100' : 'mt-0 max-h-0 opacity-0'
              }`}
            >
              {copy.bajada}
            </span>
          )}
        </span>

        {/* Pastilla de acción — `span`, no `button`: la card entera ya es el botón y
            anidar botones es HTML inválido. Sólo en la activa: en la angosta no entra.
            Entra con `animate-fade-in`, la animación del proyecto (opacidad + un
            scale mínimo): al montarse condicionalmente, una `transition` no correría
            —el elemento nace con su estilo final—, así que tiene que ser keyframes. */}
        {esActivo && (
          <span className="animate-fade-in shrink-0 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-[#16181D] shadow-sm">
            Probar modo
          </span>
        )}
      </span>
    </button>
  );
}

/**
 * Tira de modos: **todos visibles a la vez**, sin scroll horizontal. El activo se
 * expande y los demás quedan como columnas angostas; la transición es del `flex-grow`,
 * que anima suave sin reflow del resto de la home.
 *
 * Va pasando solo cada `MS_POR_MODO`. Se pausa al pasar el mouse por la tira, y el
 * modo apuntado se vuelve el activo — así se puede mirar uno sin pelearle al reloj.
 * Con `prefers-reduced-motion` no arranca el pase automático.
 *
 * Sólo el activo reproduce su carátula en video, y una sola vez (ver `CardModo`); los
 * demás muestran el póster. Con seis videos corriendo a la vez la home pesaría de más.
 */
function CarruselModos({ tema }: { tema: TemaMinimal }) {
  const navigate = useNavigate();
  const [activo, setActivo] = useState(0);
  const [pausado, setPausado] = useState(false);

  useEffect(() => {
    if (pausado) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(
      () => setActivo((i) => (i + 1) % CARRUSEL_MODOS.length),
      MS_POR_MODO,
    );
    return () => window.clearInterval(id);
  }, [pausado]);

  return (
    <section className="mt-12">
      <div
        className="flex h-[420px] gap-2.5"
        onMouseLeave={() => setPausado(false)}
      >
        {CARRUSEL_MODOS.map((modo, i) => (
          <CardModo
            key={modo.id}
            modo={modo}
            tema={tema}
            esActivo={i === activo}
            onActivar={() => {
              setPausado(true);
              setActivo(i);
            }}
            // `rutaSidebar` y no `modo.ruta` (ADR-2026-07-31-fail-closed-real-catalogo-enlaces): el `ruta` del catálogo
            // apunta a `/interfaz-v2/modo/...`, así que probar un modo desde la tira
            // expulsaba al shell viejo. Mismo mapa que usa la nav de herramientas.
            onIr={() => navigate(rutaSidebar(modo))}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Bloque "título + bajada + grilla de imágenes" — lo comparten Plantillas y
 * Creaciones recientes, que sólo se diferencian en el contenido y el destino.
 */
function GrillaImagenes({
  titulo,
  bajada,
  items,
  ruta,
  tema,
}: {
  titulo: string;
  bajada: string;
  items: ItemGaleria[];
  ruta: string;
  tema: TemaMinimal;
}) {
  const navigate = useNavigate();
  return (
    <section className="mt-12">
      <div className="flex items-start justify-between gap-4">
        <div>
          {/* Mismo cuerpo que "Ediciones rápidas": las tres secciones pesan igual. */}
          <h2 className="text-[22px] font-bold tracking-[-0.01em]">{titulo}</h2>
          <p className="mt-1 text-[13.5px] text-black/45">{bajada}</p>
        </div>
        <button
          onClick={() => navigate(ruta)}
          className="mt-1 flex shrink-0 items-center gap-1 text-[13px] text-black/55 hover:text-black"
        >
          Ver todo <span className="material-symbols-rounded text-[16px]">chevron_right</span>
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {items.map((it, i) => (
          <button
            key={`${it.src}-${i}`}
            onClick={() => navigate(ruta)}
            className={`group relative aspect-[3/4] overflow-hidden rounded-xl border border-black/[0.06] ${tema.tarjeta}`}
          >
            <img
              src={it.src}
              alt={it.alt}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            />
            {it.video && (
              <span className="absolute left-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-black/50 text-white">
                <span className="material-symbols-rounded text-[14px]">play_arrow</span>
              </span>
            )}
            {/* Nombre de la plantilla, como en `/interfaz-v2/plantillas`: overlay
                abajo a la izquierda sobre un degradé que garantiza el contraste. */}
            {it.titulo && (
              <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-2.5 pb-2 pt-8 text-left">
                <span className="block truncate text-[12.5px] font-medium text-white">
                  {it.titulo}
                </span>
                {it.subtitulo && (
                  <span className="block truncate text-[11px] text-white/70">{it.subtitulo}</span>
                )}
              </span>
            )}
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * Con `children` la pantalla deja de ser sólo la home y pasa a hacer de **shell**:
 * conserva sidebar, barra de créditos y panel dev, y en el área de contenido monta
 * lo que le pasen (`/creaciones`, `/editar-marca`). Ver ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo.
 *
 * No se extrajo el shell a un componente aparte a propósito: mover 180 líneas de
 * JSX de un árbol todavía sin commitear (ADR-2026-07-30-queda-solo-fuera-switcher-otras) es riesgo sin ganancia. Cuando
 * se commitee, el corte natural es `ShellHome` + `Home`.
 */
export default function EstiloMinimal({
  variante = 'claro',
}: { variante?: VarianteMinimal } = {}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Fade entre secciones (ADR-2026-07-31-ruta-layout-shell-sidebar-deja). El contenido se apaga, recién ahí se navega,
  // y al montar la sección nueva se vuelve a encender. La sidebar y la barra de
  // créditos quedan fuera de este div, así que ni parpadean.
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    setVisible(true);
  }, [pathname]);
  /** Navegación desde la sidebar: apaga, navega, y el efecto de arriba prende. */
  const irA = (ruta: string) => {
    if (ruta === pathname) return;
    setVisible(false);
    window.setTimeout(() => navigate(ruta), FADE_SALIDA_MS);
  };
  const { colapsada, alternar } = useSidebarColapsable();
  const tema = TEMAS[variante];

  // Contexto de sidebar RE-PROVISTO para las pantallas hospedadas (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell). Se
  // hereda el del provider real —así los setters del header mobile siguen llegando
  // a quien corresponde— y sólo se pisa la geometría, que es lo único que este
  // shell tiene distinto: su sidebar está en flujo, mide otra cosa y colapsa con su
  // propio estado. Sin esto, un panel `fixed` anclado a `sidebarWidth` se posiciona
  // contra una sidebar que en esta pantalla no existe.
  const ctxHeredado = useSidebar();
  const ctxShell = useMemo(
    () => ({
      ...ctxHeredado,
      isCollapsed: colapsada,
      sidebarWidth:
        (colapsada ? ANCHO_SIDEBAR_COLAPSADA : ANCHO_SIDEBAR) + MARGEN_SIDEBAR * 2,
    }),
    [ctxHeredado, colapsada],
  );
  // Descarte sólo en memoria: esta pantalla es una maqueta, así que la barra tiene
  // que volver al recargar (recordarlo escondería lo que se está revisando).
  const [barraDescartada, setBarraDescartada] = useState(false);

  // Catálogo de planes como overlay (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo). El estado vive en el SHELL y no en
  // la sección: los disparadores están repartidos entre los dos —la tarjeta de la
  // sidebar y la barra acá, el botón del encabezado en `HomeMinimal`— y si viviera
  // en la sección, el overlay se cerraría al cambiar de pantalla.
  const [motivoPricing, setMotivoPricing] = useState<MotivoPricing | null>(null);
  const abrirPricing = useCallback((motivo: MotivoPricing = 'mejorar') => {
    setMotivoPricing(motivo);
  }, []);

  // Marca activa REAL — misma fuente y mismo orden de preferencia que el editor
  // de marca (`config_sections/CompanyData`): el rubro de la marca activa
  // (`brand_profiles.rubro`) y, si no tiene, el de la empresa
  // (`company_profiles.rubro`). Antes era un ejemplo fijo, y el rubro del selector
  // podía no coincidir con el declarado en "Editar marca" (ADR-2026-07-31-rubro-selector-sale-marca-real).
  const { profile: perfilMarca, activeBrand: marcaActiva, loading: cargandoMarca } =
    useBrandProfile();
  const marca = {
    nombre: marcaActiva?.name ?? perfilMarca?.company_name ?? MARCA_EJEMPLO.nombre,
    rubro: marcaActiva?.rubro ?? perfilMarca?.rubro ?? MARCA_EJEMPLO.rubro,
  };

  const plan = PLAN_DEFAULT;
  const creditos = CREDITOS_DEFAULT;
  const descuento = DESCUENTO_DEFAULT;
  const planLabel = ETIQUETA_PLAN[plan] ?? plan;
  // Mismo criterio que la app real (ADR-2026-07-30-solo-criterio-saldo-toda-app): sin
  // créditos, o por debajo del 5% de la capacidad del plan. Vale para cualquier plan.
  const estadoCreditos = estadoDeCreditos(creditos, CAPACIDAD_POR_PLAN[plan]);
  const estadoBarra: 'sin' | 'pocos' =
    estadoCreditos === 'ok' ? 'sin' : estadoCreditos;
  const mostrarBarra = !barraDescartada && estadoCreditos !== 'ok';
  // Criterio del CTA de mejora, una sola vez para los dos botones que lo usan.
  const planAlDia = plan !== 'gratis' && estadoCreditos === 'ok';

  // Memo para no rehacer el objeto —y con él re-renderizar a todos los
  // consumidores— en cada render del shell.
  const cuenta = useMemo<CuentaCtx>(
    () => ({ abrirPricing, creditos, planLabel, planAlDia }),
    [abrirPricing, creditos, planLabel, planAlDia],
  );

  return (
    // El provider envuelve TODO el shell —no sólo el `<Outlet />`— para que los
    // disparadores del overlay puedan estar de los dos lados: hoy los tres de la
    // sidebar y el del encabezado de la home (ADR-2026-07-31-creditos-mejora-plan-encabezado-home).
    <CuentaContext.Provider value={cuenta}>
    {/* `estilos-cursor-scope`: manito custom de la app sobre lo clickeable (ADR-2026-07-30-minimal-plantillas-reales-secciones-misma).
        Sin la clase, el cursor custom del <body> pinta la MISMA flecha sobre botones
        y fondo, y nada se siente clickeable. */}
    <div
      className={`estilos-cursor-scope flex h-screen overflow-hidden font-sans text-[#16181D] ${tema.fondo}`}
    >
      {/* ── Sidebar flotante ────────────────────────────────────────────── */}
      <aside
        style={{ width: colapsada ? ANCHO_SIDEBAR_COLAPSADA : ANCHO_SIDEBAR }}
        className={`m-2 flex shrink-0 flex-col overflow-hidden rounded-2xl border border-black/[0.06] px-3 py-4 transition-[width] duration-300 ease-out ${tema.panel}`}
      >
        <div
          className={`mb-5 flex items-center px-1.5 ${colapsada ? 'justify-center' : 'justify-between'}`}
        >
          {/* Expandida va el lockup horizontal (isotipo + marca); colapsada no hay
              ancho para la palabra y sólo queda el botón de expandir. */}
          {!colapsada && (
            <img
              src="/images/estudio-atlas-logo-horizontal.svg"
              alt="Estudio Atlas"
              className="h-[19px] w-auto"
            />
          )}
          <button
            onClick={alternar}
            aria-label={colapsada ? 'Expandir menú' : 'Colapsar menú'}
            title={colapsada ? 'Expandir menú' : 'Colapsar menú'}
            className="grid h-7 w-7 place-items-center rounded-lg text-black/30 transition-colors hover:bg-black/[0.04] hover:text-black/60"
          >
            <span className="material-symbols-rounded text-[19px]">
              {colapsada ? 'left_panel_open' : 'left_panel_close'}
            </span>
          </button>
        </div>

        <button
          onClick={() => navigate('/mi-marca')}
          title={colapsada ? `${marca.nombre} — ${marca.rubro}` : undefined}
          className={`mb-4 flex items-center rounded-xl py-2 text-left transition-colors hover:bg-black/[0.03] ${
            colapsada ? 'justify-center px-0' : 'gap-2.5 px-2'
          }`}
        >
          {/* 35px = alto exacto de la columna de al lado (18 del nombre + 2 del gap
              + 15 del rubro), para que el avatar arranque y termine con el bloque de
              texto — ADR-2026-07-31-tarjeta-marca-sidebar-alturas-atadas.
              `rounded-full` y no `rounded-lg`: a 28px el radio de `lg` (16px en este
              config) superaba la mitad del lado y el navegador lo recortaba a un
              círculo. A 35px ya no lo supera, así que dejarlo habría convertido el
              avatar en un cuadrado redondeado sin que nadie lo pidiera. */}
          <span className="grid h-[35px] w-[35px] shrink-0 place-items-center rounded-full bg-[#EBEDF7] text-[12px] font-bold text-brand-primary">
            {marca.nombre[0]}
          </span>
          {!colapsada && (
            <>
              {/* Nombre y rubro apilados. `min-w-0` en la columna: sin eso el
                  `truncate` de los hijos no corta —un item de flex no baja de su
                  contenido— y el chevron se sale de la sidebar con nombres largos.

                  Mientras carga van dos barras grises en lugar de los textos: sin
                  eso se ve la marca de ejemplo y un instante después salta a la
                  real, que es peor que no mostrar nada. Miden lo mismo que las dos
                  líneas, así que el alto de la sidebar no se mueve. */}
              <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
                {cargandoMarca ? (
                  <>
                    {/* Los márgenes verticales llevan cada barra al alto de la línea
                        que reemplaza (12+3+3=18 y 10+2,5+2,5=15) para que el bloque
                        mida los mismos 35px cargando y cargado. */}
                    <span className="my-[3px] h-3 w-24 rounded bg-black/[0.07]" />
                    <span className="my-[2.5px] h-2.5 w-16 rounded bg-black/[0.05]" />
                  </>
                ) : (
                  <>
                    <span className="truncate text-[13.5px] font-[500] leading-[18px]">
                      {marca.nombre}
                    </span>
                    <span className="truncate text-[11.5px] font-[400] leading-[15px] text-black/45">
                      {marca.rubro}
                    </span>
                  </>
                )}
              </span>
              <span className="material-symbols-rounded text-[16px] text-black/30">unfold_more</span>
            </>
          )}
        </button>

        {/* `gap-1.5` = 6px entre ítems (ADR-2026-07-31-columna-contenido-90-mejorar-plan). */}
        <nav className="flex flex-col gap-1.5">
          {NAV_LATERAL.map((item) => (
            <button
              key={item.label}
              onClick={() => irA(item.ruta)}
              title={colapsada ? item.label : undefined}
              className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors ${
                colapsada ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-2.5'
              } ${
                // Seleccionado: ícono y texto en el azul de marca, con el fondo que
                // le corresponda a la variante (tinte azul en clara, blanco en gris).
                // Sale de la ruta actual y ya no de `i === 0`: con la nav navegando
                // de verdad entre tres pantallas (ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo), fijarlo en el primero
                // dejaba "Inicio" marcado estando en Creaciones.
                item.ruta === pathname
                  ? `${tema.seleccion} ${ITEM_ACTIVO}`
                  : 'text-black/70 hover:bg-black/[0.03]'
              }`}
            >
              <span
                className={`material-symbols-rounded shrink-0 text-[19px] ${
                  item.ruta === pathname ? '' : 'opacity-75'
                }`}
              >
                {item.icono}
              </span>
              {!colapsada && item.label}
            </button>
          ))}
        </nav>

        {/* Divisor: 16px arriba, 22px abajo, al 80% del ancho y alineado a la
            izquierda (ADR-2026-07-31-divisor-sidebar-16-22-aire). Sin `mx-auto` el flex column lo deja arrancando en
            el borde izquierdo del contenido, que ya trae el `px-3` de la sidebar. */}
        <div className="mb-[22px] ml-3 mt-4 h-px w-4/5 bg-black/[0.06]" />

        {/* Herramientas: el catálogo completo, con las rutas reales de cada modo.
            La lista sale de `contenidoAtlas`, así que sigue a `MODES` sin tocar
            esta pantalla; los nombres salen de `etiquetaSidebar`, que los toma del
            carrusel para que un modo se llame igual en los dos lados (ADR-2026-07-31-solo-nombre-modo-sidebar-toma). */}
        {/* `gap-1.5` = 6px, el MISMO que la nav de arriba (ADR-2026-07-31-columna-contenido-90-mejorar-plan): las dos listas
            son ítems de navegación del mismo peso, y separarlas distinto hacía que
            la de herramientas se leyera como un bloque más apretado. */}
        <nav className="flex flex-col gap-1.5 overflow-y-auto">
          {/* Rótulo del grupo, no un ítem: sin ícono, sin click y más chico que la
              navegación. Colapsada no hay ancho para la palabra y el divisor de
              arriba ya alcanza para separar. */}
          {!colapsada && (
            <p className="mb-2 px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-black/35">
              Todas las herramientas
            </p>
          )}
          {HERRAMIENTAS_SIDEBAR.map((h) => {
            const destino = rutaSidebar(h);
            // Las herramientas ahora son secciones de este shell (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell), así que
            // se marcan y se cruzan con el mismo fade que Inicio/Creaciones/Editar
            // marca. `startsWith` y no `===` porque el modo abre sub-rutas.
            const activa = pathname === destino || pathname.startsWith(`${destino}/`);
            return (
              <button
                key={h.id}
                onClick={() => irA(destino)}
                title={colapsada ? etiquetaSidebar(h) : undefined}
                className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors ${
                  colapsada ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-2.5'
                } ${
                  activa
                    ? `${tema.seleccion} ${ITEM_ACTIVO}`
                    : 'text-black/70 hover:bg-black/[0.03]'
                }`}
              >
                <span
                  className={`material-symbols-rounded shrink-0 text-[19px] ${
                    activa ? '' : 'opacity-75'
                  }`}
                >
                  {h.icono}
                </span>
                {!colapsada && <span className="truncate">{etiquetaSidebar(h)}</span>}
              </button>
            );
          })}
        </nav>

        {/* Pie: soporte y, cerrando la sidebar, la tarjeta de plan + créditos.
            El perfil no va como ítem suelto: es el botón de usuario de la tarjeta. */}
        <div className="mt-auto pt-4">
          {/* Soporte: ícono genérico de ayuda, no el logo de WhatsApp — el canal
              (hoy WhatsApp) es un detalle del destino, no la etiqueta. */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            title={colapsada ? 'Centro de ayuda' : undefined}
            className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] text-black/65 transition-colors hover:bg-black/[0.03] hover:text-black/85 ${
              colapsada ? 'mx-auto h-9 w-9 justify-center' : 'w-full gap-2.5 px-2.5'
            }`}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px]">support_agent</span>
            {!colapsada && 'Centro de ayuda'}
          </a>

          {/* Tarjeta de cuenta: plan vigente, créditos restantes y el CTA de mejora.
              El contador usa `token_icon.png`, el ícono de créditos oficial de la app
              (el mismo del Sidebar real y de `CreditBadge`). */}
          {colapsada ? (
            <div className="mt-2 flex flex-col items-center gap-1.5">
              <MenuCuenta variante="sidebar" etiqueta={`${NOMBRE_COMPLETO} — ${planLabel}`}>
                <AvatarUsuario />
              </MenuCuenta>
              <button
                onClick={() => abrirPricing('mejorar')}
                title="Mejorar plan"
                aria-label="Mejorar plan"
                className="grid h-9 w-9 place-items-center rounded-full bg-brand-primary text-white transition-colors hover:bg-brand-primary-hover"
              >
                <span className="material-symbols-rounded text-[19px]">bolt</span>
              </button>
            </div>
          ) : (
            // Blanca en las dos variantes: en la clara se separa del panel por el
            // borde, y en la gris es justamente la figura sobre el panel gris.
            //
            // La tarjeta ENTERA lleva al perfil (ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil). Es un `div` y no un
            // `button` porque adentro hay otros dos controles —el avatar, que abre el
            // menú de cuenta, y "Mejorar plan"—, y anidar botones es HTML inválido.
            // Los dos frenan la propagación para conservar su propia acción.
            //
            // No lleva `role="button"` ni `tabIndex`: un control que contiene otros
            // controles se anuncia mal y atrapa el foco. El click de la tarjeta es una
            // comodidad de puntero; el acceso por teclado y para lectores de pantalla
            // sigue siendo el botón "Perfil" de la derecha, que es explícito.
            <div
              onClick={() => navigate('/perfil')}
              className="mt-2 cursor-pointer rounded-2xl border border-black/[0.09] bg-white p-3 transition-colors hover:border-black/[0.14]"
            >
              <div className="flex items-center gap-2.5">
                {/* `contents`: el wrapper existe SÓLO para frenar la propagación, así
                    que no participa del layout y la fila queda igual que antes. */}
                <span className="contents" onClick={(e) => e.stopPropagation()}>
                  <MenuCuenta variante="sidebar" etiqueta={`${NOMBRE_COMPLETO} — ${planLabel}`}>
                    <AvatarUsuario />
                  </MenuCuenta>
                </span>
                {/* Plan y saldo un escalón por debajo de la navegación (ADR-2026-07-31-plan-saldo-escalon-debajo-tarjeta): son
                    el estado de la cuenta, no una acción. El que manda en la tarjeta es
                    el botón de abajo. */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-semibold leading-tight">
                    {planLabel}
                  </p>
                  <p className="mt-1 flex items-center gap-1 text-[12px] font-semibold leading-none text-brand-primary">
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="h-3 w-3"
                    />
                    <span className="tabular-nums">{creditos}</span>
                  </p>
                </div>
                {/* Acá iba un botón de perfil (ADR-2026-07-31-engranaje-tarjeta-cuenta-pasa-icono). Se sacó en ADR-2026-07-31-fuera-boton-perfil-tarjeta-cuenta: desde
                    que la tarjeta ENTERA lleva al perfil (ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil), era un tercer
                    control apretado en la misma fila para el mismo destino.
                    El acceso por teclado no se pierde: el ítem "Perfil" del menú que
                    abre el avatar de la izquierda va al mismo lado. */}
              </div>
              {/* Sólido sólo cuando hay algo que resolver: plan gratis, o saldo en
                  cero / por debajo del umbral (ADR-2026-07-31-columna-contenido-90-mejorar-plan). El criterio vive en
                  `clasesMejorarPlan`, compartido con el CTA del encabezado. */}
              <button
                onClick={(e) => {
                  // Sin esto el click sube a la tarjeta y navega al perfil en vez de
                  // abrir el catálogo (ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil).
                  e.stopPropagation();
                  abrirPricing('mejorar');
                }}
                className={`mt-3 w-full rounded-full py-2.5 text-[12.5px] font-semibold transition-colors ${clasesMejorarPlan(planAlDia)}`}
              >
                Mejorar plan
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── Contenido ─────────────────────────────────────────────────────
          `content-panel-clip` NO tiene CSS: es el hook por el que
          `utils/appScroll` y `ScrollToTop` encuentran el scroller de la app
          (ADR-171). Poniéndoselo acá, las pantallas que se montan adentro
          conservan su guardado/restaurado de scroll y el reset al cambiar de
          ruta sin tocarles una línea. `shell-home-scroll` reinterpreta sus
          `min-h-screen` (ver index.css). Ambos son ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo. */}
      <main className="relative flex-1 overflow-y-auto content-panel-clip shell-home-scroll">
        {/* La barra de créditos vive en el SHELL, arriba del contenido: es del
            usuario, no de la sección, así que no se va ni reaparece al cambiar de
            pantalla. Su contenedor replica el ancho de la columna de la home
            (ADR-2026-07-30-barra-creditos-alinea-columna-contenido). */}
        {mostrarBarra && (
          <div className="mx-auto max-w-[90%]">
            <CreditsTopBar
              variant="brand"
              className="mt-2.5 rounded-2xl"
              estado={estadoBarra}
              restantes={creditos}
              discountPercent={descuento}
              onDismiss={() => setBarraDescartada(true)}
              // El CTA abre el mismo overlay, con el copy del estado que la barra
              // acaba de anunciar — así el catálogo no contradice al cartel que lo
              // abrió (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo).
              onUpgrade={() => abrirPricing(estadoBarra)}
            />
          </div>
        )}

        {/* Sólo ESTO se desvanece al cambiar de sección: la sidebar y la barra
            quedan quietas (ADR-2026-07-31-ruta-layout-shell-sidebar-deja). El `pb-6` (24px, ADR-2026-07-31-titulos-seccion-tamano-saludo-mismo) es el aire de abajo
            para TODAS las secciones — las pantallas hospedadas traen `md:pb-0`
            porque contaban con el padding que ponía `.main-content`, que este
            shell no tiene, y sin esto terminan pegadas al borde.

            `max-w-[90%] mx-auto` es la columna de contenido de las TRES secciones
            (ADR-2026-07-31-columna-contenido-90-mejorar-plan): puesta acá y no en cada pantalla, la barra de créditos —que
            usa el mismo ancho— queda alineada con lo que hay debajo, sin que las
            pantallas hospedadas tengan que enterarse. El 5% de cada lado hace de
            padding lateral, así que la home ya no lleva el suyo. */}
        <div
          className={`shell-seccion mx-auto max-w-[90%] pb-6 transition-opacity ${
            visible
              ? 'opacity-100 duration-[680ms] ease-out'
              : 'opacity-0 duration-[240ms] ease-in'
          }`}
        >
          <SidebarContext.Provider value={ctxShell}>
            <Outlet />
          </SidebarContext.Provider>
        </div>
      </main>

      {/* Catálogo de planes como overlay (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo). Se portalea a `document.body`
          desde adentro del componente, así que da igual dónde se monte acá: no
          queda atrapado en el stacking context del shell. */}
      {motivoPricing && (
        <CreditsPricingSheet
          {...copyPricingPorMotivo(motivoPricing)}
          onClose={() => setMotivoPricing(null)}
        />
      )}
    </div>
    </CuentaContext.Provider>
  );
}

/**
 * Contenido de la home. Se separó del shell (ADR-2026-07-31-ruta-layout-shell-sidebar-deja) para que las tres secciones
 * sean rutas hijas de un layout: así la sidebar **no se desmonta** al navegar y el
 * fade puede aplicarse sólo al contenido.
 */
export function HomeMinimal({
  variante = 'claro',
}: { variante?: VarianteMinimal } = {}) {
  const navigate = useNavigate();
  const tema = TEMAS[variante];
  // Plan, créditos y el disparador del catálogo salen del shell: son los mismos
  // valores que muestra la sidebar.
  const { abrirPricing, creditos, planLabel, planAlDia } = useContext(CuentaContext);

  // Tres categorías al azar por visita, distintas de las de la vez anterior. Se
  // resuelve una sola vez por montaje: recalcularlo en cada render cambiaría las
  // plantillas ante cualquier interacción de la pantalla.
  const filasPlantillas = useMemo(() => elegirFilasPlantillas(3), []);

  // Sin ancho ni padding propios: la columna la define el shell
  // (`max-w-[90%] mx-auto`, ADR-2026-07-31-columna-contenido-90-mejorar-plan) para que las tres secciones y la barra de
  // créditos compartan exactamente el mismo ancho.
  return (
        <div>
          {/* Bienvenida y accesos de cuenta comparten fila, centrados entre sí. El
              `pt-10` deja la mitad del aire que había con la barra superior aparte. */}
          {/* Sin `pt-10`: los 40px de arriba del título los pone ahora la regla
              compartida del shell, para que las tres secciones abran igual
              (ADR-2026-07-31-titulos-seccion-tamano-saludo-mismo). */}
          <div className="flex items-center justify-between gap-6">
            <h1 className="text-[32px] font-bold leading-[1.2] tracking-[-0.02em]">
              {saludo()}, {NOMBRE_USUARIO}
              <br />
              {/* `font-[600]` y no `font-semibold`: el config del proyecto mapea
                  `semibold` a 530, no a 600. */}
              <span className="text-[26px] font-[600] text-black/40">
                ¿Qué quisieras crear hoy?
              </span>
            </h1>

            {/* Accesos de cuenta — ADR-2026-07-31-creditos-mejora-plan-encabezado-home, supersede ADR-2026-07-31-encabezado-mejorar-plan-centro-ayuda en el CTA de mejora.
                Réplica de la referencia: ayuda con etiqueta, los créditos y la mejora
                juntos en una pastilla, y el avatar al final. Los créditos y el CTA van
                pegados porque el número es lo que le da sentido a la oferta; sueltos,
                "Mejorar plan" es un botón más. */}
            <div className="flex shrink-0 items-center gap-2.5 text-[13px]">
              {/* Con etiqueta y encerrado: como texto suelto sobre el fondo no se
                  leía como un control. La superficie y el borde son los de las
                  tarjetas del resto de la pantalla, así que acompaña a la variante. */}
              {/* `h-[34px]` acá, en la pastilla de créditos y en el avatar: los tres
                  controles miden lo mismo. Con paddings verticales cada uno terminaba
                  con un alto distinto según su contenido (ícono de 18 vs texto de
                  12,5), y en una fila de tres se nota. */}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex h-[34px] items-center gap-1.5 rounded-full border border-black/[0.08] px-3.5 font-[500] text-black/65 transition-colors hover:border-black/[0.14] hover:text-black/85 ${tema.tarjeta}`}
              >
                <span className="material-symbols-rounded text-[18px]">support_agent</span>
                Centro de ayuda
              </a>

              {/* Dos lecturas del mismo bloque según el estado de la cuenta
                  (ADR-2026-07-31-pastilla-encabezado-tiene-dos-lecturas). Con algo que resolver es una OFERTA: saldo + botón sólido
                  "Mejorar plan". Sin nada que resolver es un DATO: la pastilla entera
                  informa el saldo disponible, y como ya no compite con nada el número
                  y su etiqueta se juntan. */}
              {planAlDia ? (
                <button
                  onClick={() => abrirPricing('mejorar')}
                  title={planLabel}
                  className="flex h-[34px] items-center gap-1.5 rounded-full bg-[#EBEFFE] px-3.5 text-[12.5px] font-semibold text-brand-primary transition-colors hover:bg-[#DFE6FD]"
                >
                  <img
                    src="/images/token_icon.png"
                    alt=""
                    aria-hidden="true"
                    className="h-3.5 w-3.5"
                  />
                  <span className="tabular-nums text-[13px]">{creditos}</span>
                  Créditos disponibles
                </button>
              ) : (
                <div className="flex h-[34px] items-center gap-2 rounded-full bg-[#EBEFFE] pl-3 pr-1">
                  <span
                    title={`${planLabel} — ${creditos} créditos`}
                    className="flex items-center gap-1 text-[13px] font-semibold text-brand-primary"
                  >
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="h-3.5 w-3.5"
                    />
                    <span className="tabular-nums">{creditos}</span>
                  </span>
                  {/* Mismo criterio de color que el de la sidebar (ADR-2026-07-31-encabezado-cta-comparte-criterio-sidebar). */}
                  <button
                    onClick={() => abrirPricing('mejorar')}
                    className={`h-[26px] rounded-full px-3.5 text-[12.5px] font-semibold transition-colors ${clasesMejorarPlan(planAlDia)}`}
                  >
                    Mejorar plan
                  </button>
                </div>
              )}

              {/* El avatar abre el menú de cuenta (ADR-2026-07-31-menu-cuenta-avatar-uno-solo), el mismo que el de la
                  sidebar. Sólo acá lleva aro azul: es el único de los cuatro que vive
                  sobre el fondo pelado, sin tarjeta ni panel que lo recorte, y sin él
                  se pierde contra el gris. Va como `ring` y no como `border` para no
                  achicar el círculo ni correr la inicial.

                  1 px, el mismo grosor que el borde de "Centro de ayuda", y al 22% —
                  suficiente para separarlo del fondo sin leerse como un estado
                  seleccionado.

                  El azul va escrito como `rgba` y no como `ring-brand-primary/22`:
                  `brand.primary` es `var(--brand-primary)` sin `<alpha-value>`, así que
                  Tailwind no le puede aplicar el modificador de opacidad y el aro
                  quedaba del azul por defecto del framework (ADR-2026-07-31-encabezado-cta-comparte-criterio-sidebar). */}
              <MenuCuenta variante="home" etiqueta={`${NOMBRE_COMPLETO} — ${planLabel}`}>
                <AvatarUsuario
                  px={34}
                  className="ring-1 ring-[rgba(22,68,235,0.22)] transition-shadow hover:ring-[rgba(22,68,235,0.45)]"
                />
              </MenuCuenta>
            </div>
          </div>

          {/* Carrusel de modos — los 7 de `/modos`, con su carátula real. */}
          <CarruselModos tema={tema} />

          {/* Creaciones recientes — una sola fila: es un vistazo, no el historial.
              Para verlo entero está "Ver todo". */}
          <GrillaImagenes
            titulo="Creaciones recientes"
            bajada="Lo último que generaste con Atlas."
            items={CREACIONES_RECIENTES.slice(0, 6)}
            ruta={RUTA_HISTORIAL}
            tema={tema}
          />

          {/* Plantillas — una fila por categoría, con sus ejemplos reales. */}
          <section className="mt-12">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[22px] font-bold tracking-[-0.01em]">Plantillas</h2>
                <p className="mt-1 text-[13.5px] text-black/45">
                  Escenas ya armadas: elegís una y le ponés tu producto.
                </p>
              </div>
              <button
                onClick={() => navigate(RUTA_EN_SHELL.plantillas)}
                className="mt-1 flex shrink-0 items-center gap-1 text-[13px] text-black/55 hover:text-black"
              >
                Ver todo <span className="material-symbols-rounded text-[16px]">chevron_right</span>
              </button>
            </div>

            {/* Cada categoría en su propia tarjeta, como las de "Ediciones rápidas":
                así se lee como un bloque cerrado y no como tres grillas sueltas. */}
            {filasPlantillas.map((fila) => (
              <div
                key={fila.categoria}
                className={`mt-4 rounded-2xl border border-black/[0.06] p-4 ${tema.tarjeta}`}
              >
                <div className="mb-3.5">
                  <h3 className="text-[16.5px] font-semibold tracking-[-0.01em]">
                    {fila.categoria}
                  </h3>
                  <p className="mt-0.5 text-[12.5px] text-black/45">{fila.bajada}</p>
                </div>

                {/* 5 plantillas + "Ver más" como sexta celda: el acceso a la lista
                    completa vive en la grilla, a la misma altura que lo que muestra. */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                  {fila.items.slice(0, 5).map((it) => (
                    <button
                      key={it.src}
                      onClick={() => navigate(RUTA_EN_SHELL.plantillas)}
                      className={`group relative aspect-[3/4] overflow-hidden rounded-xl border border-black/[0.06] ${tema.hueco}`}
                    >
                      <img
                        src={it.src}
                        alt={it.alt}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                      <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-2.5 pb-2 pt-8 text-left">
                        <span className="block truncate text-[12.5px] font-medium text-white">
                          {it.titulo}
                        </span>
                      </span>
                    </button>
                  ))}

                  <button
                    onClick={() => navigate(RUTA_EN_SHELL.plantillas)}
                    aria-label={`Ver todas las plantillas de ${fila.categoria}`}
                    className={`group flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-black/[0.14] transition-colors hover:border-brand-primary/40 hover:bg-[#F3F5FD] ${tema.vacio}`}
                  >
                    <span
                      className={`grid h-9 w-9 place-items-center rounded-full text-black/55 shadow-sm transition-colors group-hover:text-brand-primary ${tema.hueco}`}
                    >
                      <span className="material-symbols-rounded text-[20px]">arrow_forward</span>
                    </span>
                    <span className="text-[12.5px] font-medium text-black/60 transition-colors group-hover:text-brand-primary">
                      Ver más
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </section>

          {/* Ediciones rápidas — cierra la home: son acciones SOBRE lo ya generado,
              así que van después de las creaciones, no antes. */}
          <section className="mt-12">
            <h2 className="text-[22px] font-bold tracking-[-0.01em]">Ediciones rápidas</h2>
            <p className="mt-1 text-[13.5px] text-black/45">
              Lo que podés hacer con una imagen o video ya generado
            </p>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {EDICIONES_RAPIDAS.map(({ label, desc, Icono, ruta }) => (
                <button
                  key={label}
                  onClick={() => navigate(ruta)}
                  className={`group flex flex-col items-center justify-start rounded-2xl border border-black/[0.06] px-5 py-7 text-center transition-all hover:-translate-y-0.5 hover:border-black/[0.1] hover:shadow-[0_8px_28px_rgba(16,24,45,0.07)] ${tema.tarjeta}`}
                >
                  <span
                    className={`mb-3.5 grid h-11 w-11 place-items-center rounded-2xl text-[#16181D]/75 transition-colors group-hover:bg-[#EBEDF7] group-hover:text-brand-primary ${tema.hueco}`}
                  >
                    <Icono className="h-[19px] w-[19px]" strokeWidth={1.8} />
                  </span>
                  <span className="text-[14.5px] font-semibold">{label}</span>
                  <span className="mt-1 text-[13px] leading-relaxed text-black/45">{desc}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
  );
}
